import { useMemo, useState } from "react";
import {
	Box,
	InputAdornment,
	LinearProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";

const ellipsisSx = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	maxWidth: 240,
};

const SpreadsheetPreviewTable = ({ rows, sheetName, loading }) => {
	const [sorting, setSorting] = useState([]);
	const [globalFilter, setGlobalFilter] = useState("");

	const columns = useMemo(() => {
		const headerRow = rows[0] || [];
		return headerRow.map((cell, index) => ({
			accessorKey: `col_${index}`,
			header: cell || `Column ${index + 1}`,
			cell: (info) => {
				const value = info.getValue();
				const display = value === null || value === undefined || value === "" ? "-" : String(value);
				return (
					<Typography variant="body2" sx={ellipsisSx} title={display}>
						{display}
					</Typography>
				);
			},
		}));
	}, [rows]);

	const data = useMemo(() => {
		if (rows.length <= 1) return [];
		const columnCount = (rows[0] || []).length;
		return rows.slice(1).map((row, rowIndex) => {
			const record = { id: `preview-row-${rowIndex}` };
			for (let index = 0; index < columnCount; index += 1) {
				record[`col_${index}`] = row[index] ?? "";
			}
			return record;
		});
	}, [rows]);

	const table = useReactTable({
		data,
		columns,
		state: { sorting, globalFilter },
		onSortingChange: setSorting,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 25 } },
	});

	if (!rows.length) {
		return (
			<Box sx={{ p: 3, textAlign: "center" }}>
				<Typography variant="body2" color="text.secondary">
					Unable to preview spreadsheet content. You can still download the file.
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					flexWrap: "wrap",
					px: 2,
					py: 1.25,
					borderBottom: "1px solid",
					borderColor: "divider",
					bgcolor: "background.paper",
				}}>
				<Typography variant="caption" color="text.secondary">
					{sheetName ? `Sheet: ${sheetName}` : "Spreadsheet preview"} · first 100 rows from source file
				</Typography>
				<TextField
					size="small"
					variant="outlined"
					placeholder="Search..."
					value={globalFilter ?? ""}
					onChange={(event) => setGlobalFilter(event.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchRoundedIcon fontSize="small" />
							</InputAdornment>
						),
						sx: { borderRadius: 1.5, backgroundColor: "grey.50" },
					}}
					sx={{ minWidth: 220 }}
				/>
			</Box>
			{loading && <LinearProgress />}
			<TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
				<Table stickyHeader size="small">
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const sortDirection = header.column.getIsSorted();
									return (
										<TableCell
											key={header.id}
											align="center"
											sortDirection={sortDirection || false}
											sx={{
												backgroundColor: "grey.50",
												fontWeight: 500,
												color: "text.secondary",
												textTransform: "uppercase",
												fontSize: "0.7rem",
												letterSpacing: "0.4px",
												whiteSpace: "nowrap",
											}}>
											<TableSortLabel
												active={!!sortDirection}
												direction={sortDirection || "asc"}
												onClick={header.column.getToggleSortingHandler()}>
												{flexRender(header.column.columnDef.header, header.getContext())}
											</TableSortLabel>
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableHead>
					<TableBody>
						{table.getRowModel().rows.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} align="center">
									<Typography variant="body2" sx={{ my: 2 }} color="text.secondary">
										No rows match your search.
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							table.getRowModel().rows.map((row) => (
								<TableRow key={row.id} hover>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id} align="center" sx={{ fontSize: "0.82rem" }}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={table.getFilteredRowModel().rows.length}
				page={table.getState().pagination.pageIndex}
				onPageChange={(event, newPage) => table.setPageIndex(newPage)}
				rowsPerPage={table.getState().pagination.pageSize}
				onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
				rowsPerPageOptions={[10, 25, 50]}
				sx={{ borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
			/>
		</Box>
	);
};

export default SpreadsheetPreviewTable;
